/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  FormControl,
  FormError,
  FormField,
  FormLabel,
} from "@/components/admin/form";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { Check, ChevronsUpDown, Plus } from "lucide-react";
import type {
  ChoicesProps,
  InputProps,
  SupportCreateSuggestionOptions,
} from "ra-core";
import {
  FieldTitle,
  useChoices,
  useChoicesContext,
  useEvent,
  useGetRecordRepresentation,
  useInput,
  useSupportCreateSuggestion,
  useTranslate,
} from "ra-core";
import * as React from "react";
import { useCallback } from "react";
import { InputHelperText } from "./input-helper-text";

/**
 * Form control that lets users choose a value from a list using a dropdown with autocompletion.
 *
 * This input allows editing scalar values with a searchable dropdown interface. It supports creating
 * new choices on the fly and works seamlessly inside ReferenceInput for editing foreign key relationships.
 *
 * @see {@link https://marmelab.com/shadcn-admin-kit/docs/autocompleteinput/ AutocompleteInput documentation}
 *
 * @example
 * import {
 *   Create,
 *   SimpleForm,
 *   AutocompleteInput,
 *   ReferenceInput,
 * } from '@/components/admin';
 *
 * const PostCreate = () => (
 *   <Create>
 *     <SimpleForm>
 *       <AutocompleteInput
 *         source="category"
 *         choices={[
 *           { id: 'tech', name: 'Tech' },
 *           { id: 'lifestyle', name: 'Lifestyle' },
 *           { id: 'people', name: 'People' },
 *         ]}
 *       />
 *       <ReferenceInput label="Author" source="author_id" reference="authors">
 *         <AutocompleteInput />
 *       </ReferenceInput>
 *     </SimpleForm>
 *   </Create>
 * );
 */
export const AutocompleteInput = (
  props: Omit<InputProps, "source"> &
    Omit<SupportCreateSuggestionOptions, "handleChange" | "filter"> &
    Partial<Pick<InputProps, "source">> &
    ChoicesProps & {
      className?: string;
      disableValue?: string;
      filterToQuery?: (searchText: string) => any;
      translateChoice?: boolean;
      placeholder?: string;
      inputText?:
        | React.ReactNode
        | ((option: any | undefined) => React.ReactNode);
      matchSuggestion?: (filter: string, choice: any) => boolean;
    },
) => {
  const {
    filterToQuery = DefaultFilterToQuery,
    inputText,
    create,
    createValue,
    createLabel,
    createHintValue,
    createItemLabel,
    onCreate,
    optionText,
    matchSuggestion,
  } = props;
  const {
    allChoices = [],
    source,
    resource,
    isFromReference,
    setFilters,
  } = useChoicesContext(props);
  const { id, field, isRequired } = useInput({ ...props, source });
  const translate = useTranslate();
  const { placeholder = translate("ra.action.search", { _: "Search..." }) } =
    props;

  const getRecordRepresentation = useGetRecordRepresentation(resource);
  const { getChoiceText, getChoiceValue } = useChoices({
    optionText:
      props.optionText ?? (isFromReference ? getRecordRepresentation : "name"),
    optionValue: props.optionValue ?? "id",
    disableValue: props.disableValue,
    translateChoice: props.translateChoice ?? !isFromReference,
  });

  const [filterValue, setFilterValue] = React.useState("");

  const [open, setOpen] = React.useState(false);
  const selectedChoice = allChoices.find(
    (choice) => getChoiceValue(choice) === field.value,
  );

  // Sync filterValue with field.value when no choice matches (e.g. from DescriptionWatcher)
  React.useEffect(() => {
    if (
      !selectedChoice &&
      field.value &&
      typeof field.value === "string" &&
      !open
    ) {
      setFilterValue(field.value);
    }
  }, [field.value, selectedChoice, open]);

  const getInputText = useCallback(
    (selectedChoice: any) => {
      if (typeof inputText === "function") {
        return inputText(selectedChoice);
      }
      if (inputText !== undefined) {
        return inputText;
      }
      return getChoiceText(selectedChoice);
    },
    [inputText, getChoiceText],
  );

  const handleOpenChange = useEvent((isOpen: boolean) => {
    setOpen(isOpen);
    // Reset the filter when the popover is closed
    if (!isOpen) {
      setFilters(filterToQuery(""));
    }
  });

  const handleChange = useCallback(
    (choice: any) => {
      if (field.value === getChoiceValue(choice) && !isRequired) {
        field.onChange("");
        setFilterValue("");
        if (isFromReference) {
          setFilters(filterToQuery(""));
        }
        setOpen(false);
        return;
      }
      field.onChange(getChoiceValue(choice));
      setOpen(false);
    },
    [
      field,
      getChoiceValue,
      isRequired,
      setFilterValue,
      isFromReference,
      setFilters,
      filterToQuery,
      setOpen,
    ],
  );

  const {
    getCreateItem,
    handleChange: handleChangeWithCreateSupport,
    createElement,
    getOptionDisabled,
  } = useSupportCreateSuggestion({
    create,
    createLabel,
    createValue,
    createHintValue,
    createItemLabel,
    onCreate,
    handleChange,
    optionText,
    filter: filterValue,
  });

  // Create-new dialog (used by the `+` button when `onCreate` is a function).
  const [createDialogOpen, setCreateDialogOpen] = React.useState(false);
  const [createName, setCreateName] = React.useState("");
  const [creating, setCreating] = React.useState(false);

  const submitCreate = useEvent(async () => {
    const name = createName.trim();
    if (!name || !onCreate || creating) return;
    setCreating(true);
    try {
      const newItem = await onCreate(name);
      if (newItem) handleChange(newItem);
    } finally {
      setCreating(false);
      setCreateDialogOpen(false);
      setCreateName("");
    }
  });

  // For the `create` (ReactElement) case: no inline create item — the + button opens the dialog directly.
  // For the `onCreate` (function) case: keep the inline "Create X" item so the user can type to create.
  const createItem =
    !React.isValidElement(create) && onCreate && (filterValue !== "" || createLabel)
      ? getCreateItem(filterValue)
      : null;
  let finalChoices = allChoices;
  if (createItem) {
    finalChoices = [...finalChoices, createItem];
  }

  return (
    <>
      <FormField className={props.className} id={id} name={source}>
        {props.label !== false && (
          <FormLabel>
            <FieldTitle
              label={props.label}
              source={props.source ?? source}
              resource={resource}
              isRequired={isRequired}
            />
          </FormLabel>
        )}
        <FormControl>
          <div className="flex gap-1 items-center">
          <Popover open={open} onOpenChange={handleOpenChange}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                role="combobox"
                aria-expanded={open}
                className="w-full justify-between h-auto min-h-9 py-1.75 font-normal whitespace-normal"
              >
                {selectedChoice ? (
                  <span className="flex-1 text-left min-w-0 break-words whitespace-normal">
                    {getInputText(selectedChoice)}
                  </span>
                ) : field.value ? (
                  <span className="flex-1 text-left min-w-0 break-words whitespace-normal">
                    {typeof field.value === "string" || typeof field.value === "number"
                      ? field.value
                      : JSON.stringify(field.value)}
                  </span>
                ) : (
                  <span className="text-muted-foreground truncate flex-1 text-left min-w-0">
                    {placeholder}
                  </span>
                )}
                <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent
              className="w-[var(--radix-popover-trigger-width)] min-w-[200px] p-0"
              align="start"
            >
              {/* We handle the filtering ourselves if it's from a reference or if a matchSuggestion is provided */}
              <Command shouldFilter={!isFromReference && !matchSuggestion}>
                <CommandInput
                  placeholder="Search..."
                  value={filterValue}
                  onValueChange={(filter) => {
                    setFilterValue(filter);
                    // We don't want the ChoicesContext to filter the choices if the input
                    // is not from a reference as it would also filter out the selected values
                    if (isFromReference) {
                      setFilters(filterToQuery(filter));
                    }
                  }}
                />
                <CommandList>
                  <CommandEmpty>No matching item found.</CommandEmpty>
                  <CommandGroup>
                    {finalChoices
                      .filter((choice) => {
                        if (
                          isFromReference ||
                          !matchSuggestion ||
                          filterValue === ""
                        ) {
                          return true;
                        }
                        return matchSuggestion(filterValue, choice);
                      })
                      .map((choice) => {
                        const isCreateItem =
                          !!createItem && choice?.id === createItem.id;
                        const disabled = getOptionDisabled(choice);

                        return (
                          <CommandItem
                            key={getChoiceValue(choice)}
                            value={
                              isCreateItem
                                ? // if it's the create option, include the filter value so it is shown in the command input
                                  // characters before and after the filter value are required
                                  // to show the option when the filter value starts or ends with a space
                                  `?${filterValue}?`
                                : getChoiceValue(choice)
                            }
                            onSelect={() =>
                              handleChangeWithCreateSupport(choice)
                            }
                            disabled={disabled}
                          >
                            <Check
                              className={cn(
                                "mr-2 h-4 w-4",
                                field.value === getChoiceValue(choice)
                                  ? "opacity-100"
                                  : "opacity-0",
                              )}
                            />
                            {getChoiceText(isCreateItem ? createItem : choice)}
                          </CommandItem>
                        );
                      })}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
          {(create || onCreate) && (
            <Button
              type="button"
              variant="outline"
              size="icon"
              className="shrink-0 h-9 w-9"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (React.isValidElement(create)) {
                  // Trigger the provided create dialog element via the sentinel value
                  handleChangeWithCreateSupport(createValue ?? "@@ra-create");
                } else {
                  // For onCreate: open a dedicated create dialog
                  setCreateName(filterValue ?? "");
                  setCreateDialogOpen(true);
                }
              }}
              title="Create new"
            >
              <Plus className="h-4 w-4" />
            </Button>
          )}
          </div>
        </FormControl>
        <InputHelperText helperText={props.helperText} />
        <FormError />
      </FormField>
      {createElement}
      {!React.isValidElement(create) && onCreate && (
        <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
          <DialogContent className="sm:max-w-sm">
            <DialogHeader>
              <DialogTitle>Create new</DialogTitle>
            </DialogHeader>
            <Input
              autoFocus
              value={createName}
              onChange={(e) => setCreateName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  submitCreate();
                }
              }}
              placeholder="Type a name…"
            />
            <DialogFooter>
              <Button
                type="button"
                variant="ghost"
                onClick={() => setCreateDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button
                type="button"
                disabled={!createName.trim() || creating}
                onClick={submitCreate}
              >
                Create
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
};

const DefaultFilterToQuery = (searchText: string) => ({ q: searchText });
